
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import { Area, CaseArea, TreeNode, AreaDetails } from "@/types"
import DataDetailsModal from "@/components/CaseDetails/DataDetailsModal.vue"

import api from "@/services/api"
import TreeNew from "@/components/Config/Tree/TreeNew.vue"
import AreaTree from "./AreaTree.vue"
import { TreeNodeVisibilityResult } from "@/types/treeNodeVisibilityResult"
import { treeFilter } from "@/mixins/treeFilter"
import { DefinitionSpecialTypes } from "@/types/dataSourceItem"

const caseStore = namespace("cases")

@Component({
	components: {
		"tree": AreaTree,
		"treeNew": TreeNew,
		"data-details": DataDetailsModal
	}
})
export default class AreaSelect extends Vue {
	$refs!: {
		search: HTMLInputElement;
		viewItemDetails: DataDetailsModal;
	}

	@Prop({})
	value!: Area[];

	@Prop({})
	readonly!: boolean;

	@caseStore.State("areas")
	public areas!: TreeNode<Area>[];

	@caseStore.Mutation("setAreaFilter")
	public setAreaFilter!: (filter: string) => void;

	@Getter("getUseScalingComponent") useScalingComponent!: boolean;

	activeAreaId: string | null = null;

	viewAreaId: string | null = null;
	areaDetails: AreaDetails | null = null;

	open: boolean = false;
	filter: string = "";
	results: TreeNode<Area>[] = []
	searchResults: TreeNode<Area>[] = []
	flatTree: TreeNode<any>[] = [];
	public loading: boolean = true;
	private showTopLevelOnly: boolean = true

	private get useLargeTree (): boolean {
		return this.useScalingComponent
	}

	private setTreeVisibility (): void {
		if (this.searchResults && this.results && this.useLargeTree) {
			this.flatTree = []
			this.searchResults = []
			this.setLargeTreeVisibility()
		}
	}

	private setLargeTreeVisibility (): void {
		if (!this.results) {
			return
		}

		let treeNodeVisibilityResults: TreeNodeVisibilityResult = treeFilter.methods.setTreeVisibility(this.results)
		this.searchResults = treeNodeVisibilityResults.treeStructure
		this.flatTree = treeNodeVisibilityResults.flatList
	}

	@Watch("filter")
	private filterChanged () : void {
		this.loading = true
		try {
			if (!this.useLargeTree) {
				this.setAreaFilter(this.filter)
				return
			}

			if (this.filter === "") {
				this.showTopLevelOnly = true
				this.searchResults = this.results
				this.setTreeVisibility()
			} else {
				let filterMatches = treeFilter.methods.getFilterMatches(this.flatTree, this.filter)
				this.searchResults = treeFilter.methods.organiseFilterResults(filterMatches)

				this.showTopLevelOnly = false
			}
		} finally {
			this.loading = false
		}
	}

	private filterResultsDebounceTimeout: NodeJS.Timeout | null = null;
	private filterSearchTerm: string = "";

	public async filterResults (): Promise<void> {
		if (this.filterResultsDebounceTimeout) {
			clearTimeout(this.filterResultsDebounceTimeout)
		}

		if (this.filterSearchTerm === "") {
			this.filter = this.filterSearchTerm
		} else {
			this.filterResultsDebounceTimeout = setTimeout(async () => {
				this.filter = this.filterSearchTerm
			}, 1000)
		}
	}

	public mounted () {
		if (!this.value) {
			this.$emit("input", [])
		}
		this.results = this.areas

		if (this.useLargeTree) {
			this.setTreeVisibility()
		} else {
			this.openTreeNodes()
		}
	}

	public openTreeNodes () {
		if (this.areas) {
			this.areas.forEach((node: TreeNode<any>) => {
				this.openAllTreeChildNodes(node)
			})
		}
	}

	public openAllTreeChildNodes (item: TreeNode<any>) {
		if (item.children != null && item.children.length > 0) {
			if (item.children[0].visible !== undefined) {
				item.children.forEach(item => {
					item.visible = true
					this.openAllTreeChildNodes(item)
				})
			}
		}
	}

	private get areaPath () {
		if (!this.areaDetails) return []

		if (!this.areaDetails.path) {
			return [
				{
					id: this.areaDetails.id,
					text: this.areaDetails.title,
					active: true
				}
			]
		}

		const path = this.areaDetails.path.map(a => {
			return {
				id: a.id,
				text: a.title
			}
		})

		path.push({
			id: this.areaDetails.id,
			text: this.areaDetails.title
		})

		return path
	}

	private toggleDropdown (mousedown: MouseEvent) {
		const targetIsNotSearch = mousedown.target !== this.$refs.search
		if (targetIsNotSearch) {
			mousedown.preventDefault()
		}

		if (this.open && targetIsNotSearch) {
			this.$refs.search.blur()
		} else if (!this.readonly) {
			this.open = true
			this.$refs.search.focus()
		}
	}

	private searchBlur () {
		this.open = false
	}

	private setActiveArea (areaId: string) {
		this.activeAreaId = areaId
	}

	private addArea (addedArea: Area) {
		if (this.value.some(rootArea => rootArea.id === addedArea.id) === false) {
			this.$emit("input", this.value.concat([addedArea]))
		}
	}

	private	removeArea (removeArea: Area): void {
		const areaIndex = this.value.findIndex(area => area.id === removeArea.id)
		if (areaIndex > -1) {
			this.value.splice(areaIndex, 1)
			this.$emit("input", this.value)
		}
	}

	private async showAreaDetails (area: CaseArea) {
		const areaDefinitionId = await api.specialDefinitionId(DefinitionSpecialTypes.Area)
		this.areaDetails = await api.area(area.id)
		this.areaDetails.definitionId = areaDefinitionId

		this.viewAreaId = area.id

		this.$refs.viewItemDetails.open({
			addItem: false,
			source: null,
			definitionId: areaDefinitionId,
			showOtherCases: false,
			data: this.areaDetails,
			canEdit: false
		})
	}

	private async setArea (area: any) {
		this.viewAreaId = area.id

		let data = await api.area(area.id)
		this.$refs.viewItemDetails.setDataItem(data)
	}

	private get shouldShowTopLevelOnly (): boolean {
		return this.showTopLevelOnly
	}
}
